import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { parseISO } from "date-fns";
import { Namespace, useT, TranslationKey, useApiErrorMessage } from "@racoon/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  cn,
  Separator,
  SearchableSelect,
  useToast,
} from "@racoon/ui";

import { fromTimestamp, matchesAnySearch } from "@racoon/lib";
import { ConnectProto, ContractStatus, ErrorCode, type ApiError } from "@racoon/api";

// API Imports
import { useListContractsQuery } from "~/shared/api/contract/contract.grpc";
import {
  useDeleteContainerGroupMutation,
  useGetContainerGroupByIdQuery,
} from "~/shared/api/container-group/container-group.grpc";
import { useListOrganizationSubtreeQuery } from "~/shared/api/organization/organization.grpc";
import { zoneChanged } from "~/shared/lib/container-group-zone";
import { organizationTreeOptions } from "~/shared/lib/org-tree";
import { useListContainerTypesQuery } from "~/shared/api/container-type/container-type.grpc";
import { useSelectableCounterparties } from "../hooks/use-selectable-counterparties";
import { contractOptionLabel } from "../model/contract-label";
import { CustomerBinRow } from "./customer-bin-row";
import { ContractPeriod } from "./contract-period";
import { ResponsibleZoneField } from "./responsible-zone.field";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { foreignOwnerName } from "~/shared/lib/holding-type";
import { useWasteOperators } from "~/shared/hooks/use-waste-operators";
import { WasteOperatorSelect } from "~/shared/ui/waste-operator-select";
import { useZonesEnabled } from "~/shared/hooks/use-zones-enabled";

export type ContainerGroupForm = {
  owner_organization_id: string;
  contract_id: string;
  container_type_id: string;
  number_of_containers: number;
  start_at?: Date;
  end_at?: Date;
  /** The responsible zone in order. Submitted unset on an update that leaves it as stored. */
  service_provider_ids?: string[];
};

const defaultValues: ContainerGroupForm = {
  owner_organization_id: "",
  contract_id: "",
  container_type_id: "",
  number_of_containers: 1,
  service_provider_ids: [],
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  locationId?: number | string;
  editingGroup?: any;
  /**
   * Pins the group to one customer: the select is preselected and disabled.
   * Used where the caller already knows whose group this is — the onboarding
   * wizard, which created the customer two steps earlier.
   */
  lockedOrganizationId?: string;
  /** Pins a new group to one contract — the one the onboarding wizard just created. */
  lockedContractId?: string;
};

// Optimized backend date parser
const parseBackendDateToDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === "string") return parseISO(value);
  if (typeof value === "object" && ("seconds" in value || "nanos" in value)) {
    return fromTimestamp(value);
  }
  return undefined;
};

export const ContainerGroupModal = ({
  isOpen,
  onClose,
  onSubmit,
  locationId,
  editingGroup,
  lockedOrganizationId,
  lockedContractId,
}: Props) => {
  const t = useT();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    setError,
    getValues,
    formState: { errors },
  } = useForm<ContainerGroupForm>({
    defaultValues,
  });

  const selectedOrgId = watch("owner_organization_id");
  const selectedContractId = watch("contract_id");
  const { selectedCompanyId } = useSelectedCompany();
  const describeError = useApiErrorMessage();
  // Set when the backend refuses a zone because responsible zones are off for the provider;
  // a contract change clears it.
  const [zoneOff, setZoneOff] = useState(false);
  // Set by a contract switch: the zone keeps only what the new provider's subtree holds.
  const pruneZone = useRef(false);
  // The opened group whose stored zone the field already holds.
  const zoneFilledFor = useRef<string | null>(null);

  // The waste operator whose contracts are offered: picked, else the group's contract's, else the default.
  const wasteOperators = useWasteOperators();
  const [pickedOperatorId, setPickedOperatorId] = useState("");
  const storedOperatorId = editingGroup?.contract?.serviceProviderId
    ? String(editingGroup.contract.serviceProviderId)
    : "";
  // A locked contract is onboarding's, signed by the active company. A stored operator outside the
  // user's legal entities (a division's parent) is listed through the company's ancestors instead,
  // once those entities are known: ListContracts needs view on every provider it names.
  const companyId = selectedCompanyId ? String(selectedCompanyId) : "";
  const storedOperatorPickable = wasteOperators.options.some((o) => o.id === storedOperatorId);
  const viaAncestors = lockedContractId || (storedOperatorId && wasteOperators.ready);
  const operatorId =
    pickedOperatorId ||
    (storedOperatorPickable ? storedOperatorId : "") ||
    (viaAncestors ? companyId : storedOperatorId ? "" : wasteOperators.defaultId);

  const {
    organizations,
    selectable: selectableOrganizations,
    isLoading: isLoadingOrgs,
  } = useSelectableCounterparties(selectedOrgId);

  const { data: contractsData, isLoading: isLoadingContracts } = useListContractsQuery(
    {
      serviceProviderIds: operatorId ? [BigInt(operatorId)] : [],
      targetOrganizationIds: selectedOrgId ? [BigInt(selectedOrgId)] : [],
      status: ContractStatus.CONTRACT_STATUS_ACTIVE,
      // A division serves under its legal entity's contracts.
      includeAncestors: true,
    },
    {
      skip: !operatorId || !selectedOrgId,
    },
  );

  const { data: containerTypesData } = useListContainerTypesQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );

  const [deleteContainerGroup, { isLoading: isDeleting }] = useDeleteContainerGroupMutation();

  // The stored zone: taken from the group when it carries one, else read by id
  // (a row from the pickup-location list carries none).
  const embeddedZone: { id?: bigint | string; name?: string }[] | undefined = editingGroup
    ?.serviceProviders?.length
    ? editingGroup.serviceProviders
    : undefined;
  const editingOwnerId = editingGroup?.ownerOrganizationId || editingGroup?.owner_organization_id;
  // skipToken, not { skip }: skipped args are still built, and BigInt throws on a non-numeric id.
  const { currentData: storedGroup, isError: isStoredZoneError } = useGetContainerGroupByIdQuery(
    isOpen && editingGroup?.id && editingOwnerId && !embeddedZone
      ? { id: BigInt(editingGroup.id), ownerOrganizationId: BigInt(editingOwnerId) }
      : skipToken,
  );
  const storedZone = embeddedZone ?? storedGroup?.serviceProviders;
  // Until the stored zone is known, the field is locked and the update leaves the zone alone.
  const zoneLoaded = !editingGroup || !!embeddedZone || !!storedGroup;
  const initialZone = useMemo(() => (storedZone ?? []).map((org) => String(org.id)), [storedZone]);
  // Off for the organization, the section shows only to keep a zone stored before; hidden, the zone is never sent.
  const showZone = useZonesEnabled() || initialZone.length > 0;

  const availableContracts = contractsData?.contracts || [];
  const containerTypes = containerTypesData?.containerTypes || [];

  // Option lists for the searchable selects (ids are bigint upstream → string here)
  const contractOptions = useMemo(
    () =>
      availableContracts.map((contract) => ({
        id: String(contract.id),
        name: contract.name || contractOptionLabel(contract, t),
      })),
    [availableContracts, t],
  );

  // Deal numbers by contract id so the contract search matches name OR deal number
  const contractDealNumberById = useMemo(
    () => new Map(availableContracts.map((contract) => [String(contract.id), contract.dealNumber])),
    [availableContracts],
  );

  const filterContractOption = useCallback(
    (option: { id: string; name: string }, query: string) =>
      matchesAnySearch([option.name, contractDealNumberById.get(option.id)], query),
    [contractDealNumberById],
  );

  const selectedContainerTypeId = watch("container_type_id");
  const containerTypeOptions = useMemo(
    () =>
      containerTypes
        .filter(
          (type) =>
            type.status !== ConnectProto.ContainerTypeStatus.INACTIVE ||
            String(type.id) === selectedContainerTypeId,
        )
        // Another company of the holding made it: say whose
        .map((type) => ({
          id: String(type.id),
          name: type.name,
          secondary: foreignOwnerName(type, selectedCompanyId),
        })),
    [containerTypes, selectedContainerTypeId, selectedCompanyId],
  );

  // Memoized selected entities
  const selectedOrg = useMemo(
    () => organizations.find((o: any) => String(o.id) === selectedOrgId),
    [organizations, selectedOrgId],
  );

  const selectedContract = useMemo(
    () => availableContracts.find((c: any) => String(c.id) === selectedContractId),
    [availableContracts, selectedContractId],
  );

  // The zone holds the serving provider's subtree: the contract's provider, else the waste operator.
  const servingProviderId =
    selectedContract?.serviceProviderId ||
    pickedOperatorId ||
    editingGroup?.contract?.serviceProviderId ||
    operatorId;
  const {
    currentData: subtree,
    isFetching: isLoadingSubtree,
    isError: isSubtreeError,
  } = useListOrganizationSubtreeQuery(
    isOpen && servingProviderId ? { organizationId: BigInt(servingProviderId) } : skipToken,
  );
  // Depth-first with depth, so same-named divisions of different branches stay apart.
  const zoneOptions = useMemo(
    () => organizationTreeOptions(subtree?.organizations ?? []),
    [subtree],
  );
  // Stored divisions outside the serving provider's subtree (left by a contract move) no longer count
  // on the backend: they are neither shown nor compared, so an untouched zone is still not sent.
  const liveInitialZone = useMemo(() => {
    if (!subtree) return initialZone;
    const inSubtree = new Set(zoneOptions.map((o) => o.id));
    return initialZone.filter((id) => inSubtree.has(id));
  }, [initialZone, subtree, zoneOptions]);
  const subtreeKnown = !servingProviderId || !!subtree || isSubtreeError;
  const zoneNamesById = useMemo(
    () => new Map((storedZone ?? []).map((org) => [String(org.id), org.name ?? ""])),
    [storedZone],
  );

  useEffect(() => {
    if (!pruneZone.current || isLoadingSubtree || !subtree) return;
    pruneZone.current = false;
    const inSubtree = new Set(zoneOptions.map((o) => o.id));
    const zone = getValues("service_provider_ids") ?? [];
    if (zone.some((id) => !inSubtree.has(id))) {
      setValue(
        "service_provider_ids",
        zone.filter((id) => inSubtree.has(id)),
      );
    }
  }, [subtree, isLoadingSubtree, zoneOptions, getValues, setValue]);

  // Prepare display data (fallback to editingGroup if backend data is still loading)
  const orgNameDisplay =
    selectedOrg?.name ||
    editingGroup?.ownerOrganization?.name ||
    editingGroup?.owner_organization?.name ||
    t(Namespace.PickupLocation, "modal.selectCustomer");

  // Edit-mode entity names shown as the selected value while the lists load.
  const orgValueLabel =
    editingGroup?.ownerOrganization?.name || editingGroup?.owner_organization?.name || undefined;
  const contractValueLabel = editingGroup?.contract
    ? editingGroup.contract.name || contractOptionLabel(editingGroup.contract, t)
    : undefined;

  const activeTariffs =
    selectedContract?.tariffRateByJobType ||
    selectedContract?.tariff_rate_by_job_type ||
    editingGroup?.contract?.tariffRateByJobType ||
    editingGroup?.contract?.tariff_rate_by_job_type;

  // Handlers for popover selections
  const handleOrgSelect = useCallback(
    (orgId: string, onChange: (value: string) => void) => {
      onChange(orgId);

      // Hard reset contract and dates on organization change
      setValue("contract_id", "");
      setValue("start_at", undefined);
      setValue("end_at", undefined);
    },
    [setValue],
  );

  const handleContractSelect = useCallback(
    (contract: any, onChange: (value: string) => void) => {
      onChange(String(contract.id));
      setZoneOff(false);
      if (String(contract.serviceProviderId ?? "") !== String(servingProviderId ?? "")) {
        pruneZone.current = true;
      }

      // Hard set dates based on the selected contract
      const start = parseBackendDateToDate(contract.start_at || contract.startAt);
      const end = parseBackendDateToDate(contract.end_at || contract.endAt);
      setValue("start_at", start);
      setValue("end_at", end);
    },
    [setValue, servingProviderId],
  );

  const handleOperatorSelect = useCallback(
    (id: string) => {
      setPickedOperatorId(id);
      setZoneOff(false);
      // Another operator serves another subtree: the zone keeps only what that subtree holds.
      if (id !== String(servingProviderId ?? "")) pruneZone.current = true;
      setValue("contract_id", "");
      setValue("start_at", undefined);
      setValue("end_at", undefined);
    },
    [setValue, servingProviderId],
  );

  // Form initialization effect
  useEffect(() => {
    if (isOpen) {
      setZoneOff(false);
      setPickedOperatorId("");
      if (editingGroup) {
        const cTypeId =
          editingGroup.containerTypeId ||
          editingGroup.container_type_id ||
          editingGroup.containerType?.id ||
          editingGroup.container_type?.id ||
          "";

        reset({
          owner_organization_id: String(
            editingGroup.ownerOrganizationId || editingGroup.owner_organization_id || "",
          ),
          contract_id: String(editingGroup.contractId || editingGroup.contract_id || ""),
          container_type_id: String(cTypeId),
          number_of_containers:
            editingGroup.numberOfContainers || editingGroup.number_of_containers || 1,
          start_at: parseBackendDateToDate(
            editingGroup.contract?.start_at || editingGroup.contract?.startAt,
          ),
          end_at: parseBackendDateToDate(
            editingGroup.contract?.end_at || editingGroup.contract?.endAt,
          ),
          // The effect below fills the stored zone, again after every reset.
          service_provider_ids: [],
        });
        zoneFilledFor.current = null;
      } else {
        reset({
          ...defaultValues,
          owner_organization_id: lockedOrganizationId ?? "",
          contract_id: lockedContractId ?? "",
        });
      }
    }
  }, [isOpen, editingGroup, lockedOrganizationId, lockedContractId, reset]);

  // The stored zone may arrive after the user has started editing: only the zone field is set, once
  // per opened group, so a refetch of the group can't undo unsaved zone edits.
  useEffect(() => {
    if (!isOpen) {
      zoneFilledFor.current = null;
      return;
    }
    const key = editingGroup ? String(editingGroup.id) : null;
    if (!key || zoneFilledFor.current === key || !zoneLoaded || !subtreeKnown) return;
    zoneFilledFor.current = key;
    setValue("service_provider_ids", liveInitialZone);
  }, [isOpen, editingGroup, zoneLoaded, subtreeKnown, liveInitialZone, setValue]);

  const handleDelete = async () => {
    if (!editingGroup?.id) return;
    const ownerOrgId =
      selectedOrgId || editingGroup.ownerOrganizationId || editingGroup.owner_organization_id;
    if (!ownerOrgId) {
      toast({
        variant: "destructive",
        title: t(Namespace.Errors, "error"),
        description: t(Namespace.Errors, "somethingWentWrong"),
      });
      return;
    }
    try {
      await deleteContainerGroup({
        id: BigInt(editingGroup.id),
        ownerOrganizationId: BigInt(ownerOrgId),
      }).unwrap();
      onClose();
    } catch {
      toast({
        variant: "destructive",
        title: t(Namespace.Errors, "error"),
        description: t(Namespace.Errors, "somethingWentWrong"),
      });
    }
  };

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    await handleSubmit(async (values) => {
      const zone = values.service_provider_ids ?? [];
      try {
        await onSubmit({
          ...values,
          service_provider_ids:
            !showZone || (editingGroup && (!zoneLoaded || !zoneChanged(zone, liveInitialZone)))
              ? undefined
              : zone,
          pickup_location_id: locationId,
        });
        onClose();
      } catch (error) {
        // Zone refusals show on the field; every failure also reaches the global toaster.
        const apiError = error as ApiError;
        const invalidField =
          apiError.code === ErrorCode.ERROR_CODE_INVALID_FIELD_VALUE
            ? apiError.args?.field
            : undefined;
        const zoneField = invalidField === "service_provider_ids";
        // Zones switched off for the provider come on the settings field.
        if (invalidField === "organization_settings") {
          setZoneOff(true);
          setValue("service_provider_ids", liveInitialZone);
        } else if (
          zoneField ||
          apiError.code === ErrorCode.ERROR_CODE_CONTAINER_GROUP_SERVICE_PROVIDER_IN_USE ||
          apiError.code === ErrorCode.ERROR_CODE_CONTAINER_GROUP_SERVICE_PROVIDER_OUTSIDE_HOLDING ||
          apiError.code === ErrorCode.ERROR_CODE_CONTAINER_GROUP_ROUTE_GROUP_OUTSIDE_ZONE
        ) {
          setError("service_provider_ids", { message: describeError(apiError) });
        }
      }
    })(e);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-[776px] !p-4">
        <DialogHeader className="!p-0">
          <DialogTitle>
            {editingGroup
              ? t(Namespace.PickupLocation, "modal.editContainerGroup")
              : t(Namespace.PickupLocation, "modal.createContainerGroup")}
          </DialogTitle>
        </DialogHeader>
        <Separator />

        <form onSubmit={onFormSubmit} className="flex flex-col">
          <div className="space-y-4 pb-4">
            <div className="pt-4">
              <WasteOperatorSelect
                id="container-group-waste-operator"
                value={operatorId}
                onChange={handleOperatorSelect}
                options={wasteOperators.options}
                valueLabel={editingGroup?.contract?.serviceProvider?.name}
                loading={wasteOperators.loading}
                disabled={!editingGroup && !!lockedContractId}
              />
            </div>
            <Separator />

            {/* CUSTOMER SECTION */}
            <div className="space-y-4 pb-4 pt-4">
              <Label className="text-[12px] !font-bold !leading-tight uppercase tracking-wide">
                {t(Namespace.PickupLocation, "modal.customer")}
              </Label>
              <Controller
                control={control}
                name="owner_organization_id"
                rules={{
                  required: t(Namespace.PickupLocation, "modal.errors.requiredCustomer") as string,
                }}
                render={({ field }) => (
                  <CustomerBinRow
                    id="container-group-org"
                    organizations={selectableOrganizations}
                    value={field.value}
                    onChange={(orgId) => handleOrgSelect(orgId, field.onChange)}
                    fallbackBin={editingGroup?.ownerOrganization?.bin}
                    valueLabel={field.value ? orgValueLabel : undefined}
                    loading={isLoadingOrgs}
                    disabled={!!lockedOrganizationId}
                    invalid={!!errors.owner_organization_id}
                    error={errors.owner_organization_id?.message}
                    size="sm"
                  />
                )}
              />
            </div>
            <Separator />

            {/* CONTRACT SECTION */}
            <div className="space-y-4 pt-4">
              <Label className="text-[12px] !font-bold text-gray-800 uppercase tracking-wide flex gap-1">
                {t(Namespace.PickupLocation, "modal.contractWithCustomer")}
                <span className="text-brand-blue uppercase">
                  {orgNameDisplay !== t(Namespace.PickupLocation, "modal.selectCustomer")
                    ? orgNameDisplay
                    : ""}
                </span>
              </Label>
              <div className="grid grid-cols-2 gap-16 items-start">
                <div className="flex flex-col gap-1 min-w-0 w-full">
                  <Controller
                    control={control}
                    name="contract_id"
                    rules={{
                      required: t(
                        Namespace.PickupLocation,
                        "modal.errors.requiredContract",
                      ) as string,
                    }}
                    render={({ field }) => (
                      <SearchableSelect
                        id="container-group-contract"
                        value={field.value}
                        options={contractOptions}
                        onChange={(contractId) => {
                          const contract = availableContracts.find(
                            (c) => String(c.id) === contractId,
                          );
                          if (contract) handleContractSelect(contract, field.onChange);
                        }}
                        filterOption={filterContractOption}
                        disabled={!selectedOrgId || (!editingGroup && !!lockedContractId)}
                        loading={isLoadingContracts}
                        valueLabel={field.value ? contractValueLabel : undefined}
                        placeholder={t(Namespace.PickupLocation, "modal.selectContract")}
                        searchPlaceholder={t(Namespace.PickupLocation, "modal.searchContract")}
                        invalid={!!errors.contract_id}
                      />
                    )}
                  />
                  {errors.contract_id && (
                    <span className="text-xs text-red-500">{errors.contract_id.message}</span>
                  )}
                </div>

                <ContractPeriod startAt={watch("start_at")} endAt={watch("end_at")} size="sm" />
              </div>
            </div>

            {/* TARIFFS SECTION */}
            {activeTariffs && Object.keys(activeTariffs).length > 0 && (
              <div className="space-y-4 pb-4">
                <Label className="text-[12px] !font-bold text-gray-800 uppercase tracking-wide">
                  {t(Namespace.PickupLocation, "modal.tariffsByJobType")}
                </Label>
                <div className="grid grid-cols-2 gap-x-16 gap-y-6">
                  {Object.entries(activeTariffs).map(([jobType, rate]) => (
                    <div key={jobType} className="flex items-center gap-3">
                      <Label className="w-1/3 text-sm font-medium text-gray-900">
                        {t(Namespace.Enum, jobType as TranslationKey)}
                      </Label>
                      <div className="relative w-2/3">
                        <Input value={String(rate)} readOnly disabled className="!bg-[#F2F4F7]" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">
                          {jobType.includes("DISINFECTION")
                            ? t(Namespace.PickupLocation, "modal.perRequest")
                            : t(Namespace.PickupLocation, "modal.perCube")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Separator />

            {/* CONTAINER INFO SECTION */}
            <div className="space-y-4 pt-4">
              <Label className="text-[12px] !font-bold text-gray-800 uppercase tracking-wide">
                {t(Namespace.PickupLocation, "modal.containerInfo")}
              </Label>
              <div className="grid grid-cols-2 gap-16 items-start">
                <div className="flex items-center gap-3 h-10 w-full">
                  <Label className="whitespace-nowrap text-sm font-medium text-gray-900">
                    {t(Namespace.PickupLocation, "modal.containerType")}
                  </Label>
                  <div className="flex flex-col w-full gap-1">
                    <Controller
                      control={control}
                      name="container_type_id"
                      rules={{
                        required: t(
                          Namespace.PickupLocation,
                          "modal.errors.requiredType",
                        ) as string,
                      }}
                      render={({ field }) => (
                        <SearchableSelect
                          id="container-group-type"
                          value={field.value}
                          options={containerTypeOptions}
                          onChange={field.onChange}
                          placeholder={t(Namespace.PickupLocation, "modal.selectType")}
                          searchPlaceholder={t(Namespace.PickupLocation, "modal.selectType")}
                          invalid={!!errors.container_type_id}
                        />
                      )}
                    />
                    {errors.container_type_id && (
                      <span className="text-xs text-red-500">
                        {errors.container_type_id.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 h-10 w-full">
                  <Label className="whitespace-nowrap text-sm font-medium text-gray-900 ml-4">
                    {t(Namespace.PickupLocation, "modal.count")}
                  </Label>
                  <div className="flex flex-col w-full gap-1">
                    <Input
                      type="number"
                      min={1}
                      aria-invalid={!!errors.number_of_containers}
                      className={cn(
                        "h-10 bg-white",
                        errors.number_of_containers ? "border-red-500 focus:ring-red-500" : "",
                      )}
                      {...register("number_of_containers", {
                        valueAsNumber: true,
                        min: {
                          value: 1,
                          message: t(Namespace.PickupLocation, "modal.errors.minCount") as string,
                        },
                        required: t(
                          Namespace.PickupLocation,
                          "modal.errors.requiredCount",
                        ) as string,
                      })}
                    />
                    {errors.number_of_containers && (
                      <span className="text-xs text-red-500">
                        {errors.number_of_containers.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {showZone && (
              <>
                <Separator />

                {/* RESPONSIBLE ZONE SECTION */}
                <div className="space-y-4 pt-4">
                  <Label className="text-[12px] !font-bold text-gray-800 uppercase tracking-wide">
                    {t(Namespace.PickupLocation, "modal.zone.title")}
                  </Label>
                  <Controller
                    control={control}
                    name="service_provider_ids"
                    render={({ field }) => (
                      <ResponsibleZoneField
                        value={field.value ?? []}
                        onChange={field.onChange}
                        options={zoneOptions}
                        fallbackNames={zoneNamesById}
                        loading={isLoadingSubtree || (!zoneLoaded && !isStoredZoneError)}
                        disabled={zoneOff || !zoneLoaded || !subtreeKnown}
                        error={
                          zoneOff
                            ? t(Namespace.PickupLocation, "modal.zone.notEnabled")
                            : !zoneLoaded && isStoredZoneError
                              ? t(Namespace.PickupLocation, "modal.zone.loadFailed")
                              : errors.service_provider_ids?.message
                        }
                      />
                    )}
                  />
                </div>
              </>
            )}
          </div>

          <Separator />

          <div className="p-0 pt-4 border-t border-gray-100 flex justify-between items-center w-full">
            {editingGroup ? (
              <Button
                id="edit-pickup-location-archive-button"
                type="button"
                variant="opacityRedWithOutline"
                className="px-8"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {t(Namespace.PickupLocation, "delete")}
              </Button>
            ) : (
              <div />
            )}

            <Button type="submit" className="bg-surface-action px-3" disabled={isDeleting}>
              {editingGroup
                ? t(Namespace.PickupLocation, "modal.save")
                : t(Namespace.PickupLocation, "modal.create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
